/**
 * Fonctions d'aide pour interagir avec les fonctions serverless Netlify.
 * Ces fonctions remplacent les appels directs à l'API Airtable.
 */
import { dispatchSessionExpiredEvent } from '../utils/sessionExpiredEvent';

/**
 * Appelle une fonction serverless pour récupérer des données (GET).
 * @param {string} endpoint Le nom de la fonction serverless.
 * @returns {Promise<any>} Les données JSON retournées par la fonction.
 */
const fetchData = async (endpoint) => {
  const token = localStorage.getItem('auth_token');
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`/.netlify/functions/${endpoint}`, { headers });
  if (response.status === 401) {
    dispatchSessionExpiredEvent();
    throw new Error('Session expirée');
  }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Erreur API (${endpoint}): ${errorData.error || response.statusText}`);
  }
  return response.json();
};

/**
 * Appelle une fonction serverless pour envoyer des données (POST).
 * @param {string} endpoint Le nom de la fonction serverless.
 * @param {object} body Le corps de la requête à envoyer.
 * @returns {Promise<any>} Les données JSON retournées par la fonction.
 */
const postData = async (endpoint, body) => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`/.netlify/functions/${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (response.status === 401) {
    dispatchSessionExpiredEvent();
    throw new Error('Session expirée');
  }
  if (!response.ok) {
    // Essayer de lire la réponse comme du texte pour avoir plus de détails
    const errorText = await response.text();
    try {
      // Tenter de parser comme JSON si possible
      const errorData = JSON.parse(errorText);
      throw new Error(`Erreur API (${endpoint}): ${errorData.error || response.statusText}`);
    } catch (e) {
      // Si ce n'est pas du JSON, afficher le texte brut de l'erreur
      throw new Error(`Erreur serveur (${endpoint}): ${errorText}`);
    }
  }
  return response.json();
};

/**
 * Appelle une fonction serverless avec une méthode spécifique (PUT, DELETE, etc.)
 * @param {string} endpoint Le nom de la fonction serverless.
 * @param {string} method La méthode HTTP (PUT, DELETE, etc.)
 * @param {object} params Les paramètres (query params ou body selon la méthode)
 * @returns {Promise<any>} Les données JSON retournées par la fonction.
 */
const callWithMethod = async (endpoint, method, params = {}) => {
  const token = localStorage.getItem('auth_token');
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let url = `/.netlify/functions/${endpoint}`;
  let options = { method, headers };

  if (method === 'DELETE' || method === 'GET') {
    // Pour DELETE et GET, passer les params en query string
    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(params).toString();
      url += `?${queryString}`;
    }
  } else {
    // Pour PUT, PATCH, etc., passer en body
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(params);
  }

  const response = await fetch(url, options);
  if (response.status === 401) {
    dispatchSessionExpiredEvent();
    throw new Error('Session expirée');
  }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Erreur API (${endpoint}): ${errorData.error || response.statusText}`);
  }
  return response.json();
};

// Récupérer tous les temps saisis
export const getTemps = async () => {
  try {
    // La logique d'enrichissement est maintenant dans la fonction serverless
    return fetchData('getTemps');
  } catch (error) {
    console.error('Erreur getTemps:', error);
    throw error;
  }
};

// Créer un nouveau temps
export const createTemps = async (data, clientId = null) => {
  // La logique de mise à jour des factures et TMP est maintenant côté serveur
  // ou doit être déclenchée séparément si nécessaire.
  // Pour l'instant, on se contente de créer le temps.
  const fields = { ...data };
  if (clientId) {
    fields['Client final'] = [clientId];
  }
  return postData('createTemps', { fields });
};

// Mettre à jour un temps existant
export const updateTemps = async (recordId, data, clientId = null) => {
  const fields = { ...data };
  if (clientId) {
    fields['Client final'] = [clientId];
  }
  return postData('updateTemps', { id: recordId, fields });
};

// Supprimer un temps
export const deleteTemps = async (recordId) => {
  return postData('deleteTemps', { id: recordId });
};

// Vérifier si un temps peut être saisi (facture en brouillon ou inexistante)
export const canCreateTemps = async (annee, mois, prestataireId) => {
  // Cette logique est complexe et doit être gérée côté serveur.
  // Pour l'instant, nous laissons le client faire des appels simples.
  // Une fonction serverless `canCreateTemps` serait nécessaire pour une implémentation complète.
  return { canCreate: true, reason: null };
};

// Récupérer les consultants
export const getConsultants = async () => {
  return fetchData('getConsultants');
};

export const createConsultant = (data)     => postData('createConsultant', data);
export const updateConsultant = (id, data) => postData('updateConsultant', { id, ...data });
export const deleteConsultant = (id)       => postData('deleteConsultant', { id });
export const saveConsultantQontoCredentials = (consultantId, login, secretKey) =>
  postData('saveConsultantQontoCredentials', { consultantId, login, secretKey });
export const getQontoGieStatus = () => fetchData('getQontoGieStatus');

// Récupérer les projets
export const getProjets = async () => {
  return fetchData('getProjets');
};

export const createProjet  = (data)       => postData('createProjet', data);
export const updateProjet  = (id, data)   => postData('updateProjet', { id, ...data });
export const deleteProjet  = (id)         => postData('deleteProjet', { id });

// Récupérer les statistiques globales
export const getStats = async () => { // Gardé pour compatibilité si utilisé ailleurs
  try {
    const temps = await getTemps();
    
    const stats = {
      caTotal: 0,
      joursTotal: 0,
      nbLignes: temps.length,
      caMoyen: 0
    };
    temps.forEach(record => {
      const ca = record.fields['CA calculé'] || 0;
      const jours = record.fields['Nombre de jours'] || 0;
      stats.caTotal += ca;
      stats.joursTotal += jours;
    });
    stats.caMoyen = stats.joursTotal > 0 ? stats.caTotal / stats.joursTotal : 0;
    return stats;
  } catch (error) {
    console.error('Erreur lors du calcul des stats:', error);
    throw error;
  }
};

export const calculateStats = (temps) => {
  const stats = {
    caTotal: 0,
    joursTotal: 0,
    nbLignes: temps.length,
    caMoyen: 0
  };
  temps.forEach(record => {
    const ca = record.fields['CA calculé'] || 0;
    const jours = record.fields['Nombre de jours'] || 0;
    stats.caTotal += ca;
    stats.joursTotal += jours;
  });
  stats.caMoyen = stats.joursTotal > 0 ? stats.caTotal / stats.joursTotal : 0;
  return stats;
};

export const calculateCaParMois = (temps) => {
  const caParMois = {};
  
  temps.forEach(record => {
    const annee = record.fields.Année;
    const mois = record.fields.Mois;
    const ca = record.fields['CA calculé'] || 0;
    
    if (annee && mois) {
      const key = `${annee}-${String(mois).padStart(2, '0')}`;
      if (!caParMois[key]) {
        caParMois[key] = {
          periode: key,
          mois: mois,
          annee: annee,
          ca: 0
        };
      }
      caParMois[key].ca += ca;
    }
  });
  
  return Object.values(caParMois).sort((a, b) => {
    if (a.annee !== b.annee) return a.annee - b.annee;
    return a.mois - b.mois;
  });
};

export const calculateCaParConsultant = (temps) => {
  const caParConsultant = {};
  
  temps.forEach(record => {
    const consultant = record.consultantNom;
    const ca = record.fields['CA calculé'] || 0;
    const jours = record.fields['Nombre de jours'] || 0;
    
    if (consultant && consultant !== '-') {
      if (!caParConsultant[consultant]) {
        caParConsultant[consultant] = { nom: consultant, ca: 0, jours: 0 };
      }
      caParConsultant[consultant].ca += ca;
      caParConsultant[consultant].jours += jours;
    }
  });
  
  return Object.values(caParConsultant);
};

// Récupérer tous les calculs TMP
export const getCalculsTMP = async () => {
  return fetchData('getCalculsTMP');
};

// Récupérer les clients finaux
export const getClients = async () => {
  return fetchData('getClients');
};

export const createClient  = (data)       => postData('createClient', data);
export const updateClient  = (id, data)   => postData('updateClient', { id, ...data });
export const deleteClient  = (id)         => postData('deleteClient', { id });

// Récupérer les prestataires porteurs
export const getPrestataires = async () => {
  return fetchData('getPrestataires');
};

export const createPrestataire  = (data)       => postData('createPrestataire', data);
export const updatePrestataire  = (id, data)   => postData('updatePrestataire', { id, ...data });
export const deletePrestataire  = (id)         => postData('deletePrestataire', { id });

// ========== TMP AJUSTÉS ==========

// Récupérer tous les TMP ajustés
export const getTMPAjustes = async () => {
  return fetchData('getTMPAjustes');
};

// Créer un TMP ajusté
export const createTMPAjuste = async (consultantId, calculTmpId, tauxAjuste, nomIdentifiant) => {
  const fields = {
    'Nom': nomIdentifiant,
    'Consultant': [consultantId],
    'Période': [calculTmpId],
    'Taux ajusté': tauxAjuste
  };
  return postData('createTMPAjuste', { fields });
};

// Mettre à jour un TMP ajusté
export const updateTMPAjuste = async (recordId, tauxAjuste) => {
  const fields = { 'Taux ajusté': tauxAjuste };
  return postData('updateTMPAjuste', { id: recordId, fields });
};

// Supprimer un TMP ajusté
export const deleteTMPAjuste = async (recordId) => {
  return postData('deleteTMPAjuste', { id: recordId });
};

// ========== CHARGES ==========

// Récupérer toutes les charges
export const getCharges = async () => {
  return fetchData('getCharges');
};

// Créer une charge
export const createCharge = async (data) => {
  return postData('createCharge', data);
};

// Mettre à jour une charge
export const updateCharge = async (recordId, data) => {
  return callWithMethod('updateCharge', 'PUT', { id: recordId, ...data });
};

// Supprimer une charge
export const deleteCharge = async (recordId) => {
  return callWithMethod('deleteCharge', 'DELETE', { id: recordId });
};

// Copier les charges d'une année à une autre
export const copyCharges = async (fromYear, toYear) => {
  return postData('copyCharges', { anneeSource: fromYear, anneeCible: toYear });
};

// Récupérer la configuration des consultants
export const getConsultantConfig = async () => {
  return fetchData('getConsultantConfig');
};

// Mettre à jour la configuration des consultants
export const updateConsultantConfig = async (annee, consultantIds) => {
  return postData('updateConsultantConfig', { annee, consultantIds });
};

// ========== FACTURATION ==========

// Récupérer les factures GIE → Prestataires
export const getFacturesExternes = async () => {
  return fetchData('getFacturesExternes');
};

// Récupérer les factures Consultants → GIE
export const getFacturesInternes = async () => {
  return fetchData('getFacturesInternes');
};

// Mettre à jour une facture externe
export const updateFactureExterne = async (recordId, data) => {
  return postData('updateFactureExterne', { id: recordId, fields: data });
};

// Mettre à jour une facture interne
export const updateFactureInterne = async (recordId, data) => {
  return postData('updateFactureInterne', { id: recordId, fields: data });
};

// Créer ou mettre à jour une facture interne
export const createOrUpdateFactureInterne = async (recordId, consultantNom, annee, trimestre, montantHT, tauxTVA, intitule) => {
  return postData('createOrUpdateFactureInterne', {
    recordId,
    consultantNom,
    annee,
    trimestre,
    montantHT,
    tauxTVA,
    intitule
  });
};

// Télécharger une facture interne (génération PDF)
export const downloadFactureInterne = async (consultantNom, annee, trimestre) => {
  return postData('downloadFactureInterne', {
    consultantNom,
    annee,
    trimestre
  });
};

// Récupérer les détails d'une facture externe
export const getFactureDetails = async (numeroFacture) => {
  const token = localStorage.getItem('auth_token');
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`/.netlify/functions/getFactureDetails?numeroFacture=${numeroFacture}`, { headers });
  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Erreur API (getFactureDetails): ${errorData.error || response.statusText}`);
  }
  return response.json();
};

// ========== PAIEMENTS FACTURES INTERNES ==========

// Récupérer les paiements d'une facture interne
export const getPaymentsForFacture = async (factureId) => {
  const token = localStorage.getItem('auth_token');
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`/.netlify/functions/getPaymentsForFacture?factureId=${factureId}`, { headers });
  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Erreur API (getPaymentsForFacture): ${errorData.error || response.statusText}`);
  }
  return response.json();
};

// Ajouter un paiement à une facture interne
export const addPayment = async (paymentData) => {
  return postData('addPaymentToFacture', paymentData);
};

// Modifier un paiement
export const updatePayment = async (paymentData) => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch('/.netlify/functions/updatePayment', {
    method: 'PUT',
    headers,
    body: JSON.stringify(paymentData)
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Erreur API (updatePayment): ${errorData.error || response.statusText}`);
  }
  return response.json();
};

// Supprimer un paiement
export const deletePayment = async (paymentId) => {
  const token = localStorage.getItem('auth_token');
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`/.netlify/functions/deletePayment?paymentId=${paymentId}`, {
    method: 'DELETE',
    headers
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Erreur API (deletePayment): ${errorData.error || response.statusText}`);
  }
  return response.json();
};

// Recalculer TOUS les Calcul TMP (à utiliser après une reprise de données)
export const recalculerTousLesTMP = async () => {
  return postData('recalculerTousLesTMP', {});
};

// Recalculer le TMP d'un trimestre spécifique
export const recalculerTMPTrimestre = async (annee, trimestre) => {
  return postData('recalculerTMPTrimestre', { annee, trimestre });
};

// ========================================
// GESTION DES UTILISATEURS
// ========================================

// Récupérer tous les utilisateurs (super admin only)
export const getUsers = async () => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch('/.netlify/functions/getUsers', {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Erreur lors de la récupération des utilisateurs');
  }

  const data = await response.json();
  return Array.isArray(data) ? data : (data.users || []);
};

// Créer un nouvel utilisateur (super admin only)
export const createUser = async (userData) => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch('/.netlify/functions/createUser', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(userData)
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Erreur lors de la création de l\'utilisateur');
  }

  return response.json();
};

// Mettre à jour un utilisateur (super admin only)
export const updateUser = async (userId, userData) => {
  const token = localStorage.getItem('auth_token');
  // Le backend attend `consultantId` (camelCase) ; on remplace `consultant_id` si présent.
  const { consultant_id, ...rest } = userData;
  const payload = { userId, ...rest };
  if ('consultant_id' in userData) payload.consultantId = consultant_id || null;
  const response = await fetch('/.netlify/functions/updateUser', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Erreur lors de la mise à jour de l\'utilisateur');
  }

  return response.json();
};

// Supprimer un utilisateur (super admin only)
export const deleteUser = async (userId) => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch('/.netlify/functions/deleteUser', {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ userId })
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Erreur lors de la suppression de l\'utilisateur');
  }

  return response.json();
};

// Changer le mot de passe de l'utilisateur connecté
export const updatePassword = async (currentPassword, newPassword) => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch('/.netlify/functions/updatePassword', {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ currentPassword, newPassword })
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Erreur lors du changement de mot de passe');
  }

  return response.json();
};

// Forcer un utilisateur à changer son mot de passe
export const forcePasswordChange = async (userId, forcePasswordChange) => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch('/.netlify/functions/forcePasswordChange', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ userId, forcePasswordChange })
  });

  if (response.status === 401) { dispatchSessionExpiredEvent(); throw new Error('Session expirée'); }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Erreur lors de la mise à jour');
  }

  return response.json();
};